import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  EyeOff, 
  Calendar,
  MapPin,
  Users, 
  Clock, 
  Trophy, 
  Target,
  Lightbulb,
  Layout,
  CheckCircle,
  AlertTriangle,
  Shield,
  RefreshCw,
  ArrowLeft,
  Plus
} from 'lucide-react';
import { renderValue } from './GamePlanRenderer';
import { SubstitutionStrategy } from './SubstitutionStrategy';
import { supabase } from '../../lib/supabase';
import type { Player, Team } from '../../types';
import { PlayerAvailabilityComponent } from '../PlayerAvailabilityComponent';
import { getGamePlanTitle } from '../../utils/gamePlanUtils';

interface GamePlanDetailProps {
  gamePlan: any;
  allPlayers: Player[];
  onEdit: () => void;
  onRegenerate?: () => void;
  onBackToList?: () => void;
  onPlayerUpdate?: (playerId: string, updates: Partial<Player>) => void;
}

export const GamePlanDetail: React.FC<GamePlanDetailProps> = ({ 
  gamePlan,
  allPlayers,
  onEdit,
  onBackToList,
  onPlayerUpdate
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basicInfo: false,
    offensiveStrategy: false,
    defensiveStrategy: false,
    specialTeams: false,
    substitutionStrategy: false,
    playerAvailability: false
  });

  const [unavailablePlayers, setUnavailablePlayers] = useState<Set<string>>(new Set());
  const [depthChart, setDepthChart] = useState<Array<{
    id: string;
    team_id: string;
    player_id: string;
    position: string;
    depth: number;
    unit: string;
    color: string;
    formation?: string;
  }>>([]);
  const [activeTab, setActiveTab] = useState('offense');

  const toggleSection = (sectionId: string, event: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent any default behavior
    event.preventDefault();
    event.stopPropagation();
    
    // Get the current scroll position
    const currentScrollY = window.scrollY;
    
    // Check if we're expanding (currently collapsed)
    const isCurrentlyCollapsed = !expandedSections[sectionId];
    
    // Update the state
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    
    // Handle scrolling after state update
    if (isCurrentlyCollapsed) {
      // If expanding, scroll to the section after a delay
      setTimeout(() => {
        const sectionElement = document.getElementById(sectionId);
        if (sectionElement) {
          const elementTop = sectionElement.offsetTop;
          window.scrollTo({
            top: elementTop - 20,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // If collapsing, maintain scroll position
      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 50);
    }
  };

  // Get MPR players for substitution strategy (for SubstitutionStrategy component)
  const mprPlayers = allPlayers.filter(player => player.playParticipation?.hasMPR);
  const healthyMprPlayers = mprPlayers.filter(player => !unavailablePlayers.has(player.id));

  useEffect(() => {
    const fetchDepthChart = async () => {
      try {
        const { data, error } = await supabase
          .from('depth_chart')
          .select('*')
          .eq('team_id', gamePlan.team_id);

        if (error) throw error;
        setDepthChart(data || []);
      } catch (error) {
        console.error('Error fetching depth chart:', error);
      }
    };

    fetchDepthChart();
  }, [gamePlan.team_id]);

  const Section = ({ 
    id, 
    title, 
    icon, 
    children, 
    defaultExpanded = true 
  }: { 
    id: string; 
    title: string; 
    icon: React.ReactNode; 
    children: React.ReactNode;
    defaultExpanded?: boolean;
  }) => {
    const isExpanded = expandedSections[id] ?? defaultExpanded;
    
    return (
      <div id={id} className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div
          onClick={(event) => toggleSection(id, event)}
          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSection(id, e as any);
            }
          }}
        >
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-base font-semibold">{title}</h3>
          </div>
          {isExpanded ? <EyeOff size={18} /> : <Eye size={18} />}
        </div>
        
        {isExpanded && (
          <div className="p-3">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-game-plan-detail="true">
      {/* Navigation Header */}
      <div className="space-y-2 mt-4">
        {onBackToList && (
              <button
                onClick={onBackToList}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back</span>
              </button>
            )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">
              {getGamePlanTitle(gamePlan, gamePlan.team)}
            </h2>
          </div>
          
            <button
              onClick={onEdit}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Edit
            </button>
        </div>
      </div>

      <Section id="basicInfo" title="Basic Information" icon={<Calendar className="w-5 h-5 text-gray-500" />}>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                <div className="text-xs text-gray-500">Date</div>
                <div className="text-sm font-medium">{new Date(gamePlan.game_date).toLocaleDateString()}</div>
                </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
                <div>
                <div className="text-xs text-gray-500">Time</div>
                <div className="text-sm font-medium">{gamePlan.game_time || 'TBD'}</div>
                </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Location</div>
                <div className="text-sm font-medium">{gamePlan.location || 'TBD'}</div>
              </div>
            </div>
          </div>
            </div>
        </Section>

          <Section 
        id="offensiveStrategy" 
            title="Offensive Strategy" 
        icon={<Target className="w-5 h-5 text-blue-500" />}
          >
        {renderValue(gamePlan.offensive_strategy, 'offensive_strategy')}
          </Section>

          <Section 
        id="defensiveStrategy" 
            title="Defensive Strategy" 
        icon={<Target className="w-5 h-5 text-red-500" />}
          >
        {renderValue(gamePlan.defensive_strategy, 'defensive_strategy')}
          </Section>

      {/* <Section 
        id="specialTeams" 
        title="Special Teams" 
        icon={<Trophy className="w-5 h-5 text-yellow-500" />}
          >
        {renderValue(gamePlan.special_teams, 'special_teams')}
      </Section> */}

      {/* Player Availability Section */}
      {allPlayers.length > 0 && (
        <Section 
          id="player_availability" 
          title="Player Availability"
          icon={<Users className="w-5 h-5" />}
          defaultExpanded={false}
        >
          <PlayerAvailabilityComponent
            players={allPlayers}
            teamId={gamePlan.team_id}
            unavailablePlayers={unavailablePlayers}
            onUnavailablePlayersChange={setUnavailablePlayers}
            onPlayerUpdate={onPlayerUpdate}
          />
        </Section>
      )}

      {/* Substitution Strategy Section */}
      <Section
        id="substitution_strategy"
        title="Substitution Strategy"
        icon={<RefreshCw className="w-5 h-5" />}
      >
        <SubstitutionStrategy 
          teamId={gamePlan.team_id}
          currentTeam={gamePlan.team}
          allPlayers={allPlayers}
          unavailablePlayers={unavailablePlayers}
          depthChart={depthChart}
          activeTab={activeTab}
        />
      </Section>
    </div>
  );
}; 